import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  EmpathyTrendChart                                                         */
/*  Pure-SVG team-average line + per-persona spark area chart.                */
/*  No dep — sized via parent container.                                      */
/* -------------------------------------------------------------------------- */

const PALETTE = [
  '#8B3FC5', '#5E1F8B', '#7A5A00', '#5b4500',
  '#0ea5e9', '#d946ef', '#ec4899', '#a855f7',
  '#14b8a6', '#84cc16', '#ef4444', '#0891b2',
  '#6366f1', '#f59e0b', '#22c55e',
];

function fmtDay(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: '2-digit' }); }
  catch { return d.slice(5); }
}

function buildPath(points, width, height, padX, padY, yMin, yMax) {
  if (!points.length) return '';
  const xScale = (i) => padX + (i / Math.max(1, points.length - 1)) * (width - padX * 2);
  const yScale = (v) => {
    if (v == null || isNaN(v)) return null;
    const t = (v - yMin) / Math.max(1, yMax - yMin);
    return padY + (1 - t) * (height - padY * 2);
  };
  let path = '';
  let started = false;
  points.forEach((p, i) => {
    const y = yScale(p);
    if (y == null) { started = false; return; }
    const cmd = !started ? 'M' : 'L';
    started = true;
    path += `${cmd}${xScale(i).toFixed(1)},${y.toFixed(1)} `;
  });
  return path.trim();
}

export default function EmpathyTrendChart({ data, height = 260 }) {
  const [hidden, setHidden] = useState(new Set());

  const { teamSeries, personaSeries, dates, personas, yMin, yMax } = useMemo(() => {
    const buckets = data?.buckets || [];
    const dates = buckets.map((b) => b.date);
    const team = buckets.map((b) => (b.team_avg == null ? null : Number(b.team_avg)));
    const personas = data?.personas || [];
    const perPersona = {};
    personas.forEach((p) => {
      perPersona[p] = buckets.map((b) => {
        const v = b.by_persona?.[p];
        return v == null ? null : Number(v);
      });
    });
    return {
      teamSeries: team,
      personaSeries: perPersona,
      dates,
      personas,
      yMin: 0,
      yMax: 100,
    };
  }, [data]);

  const width = 760;
  const padX = 40;
  const padY = 18;

  if (!data || !dates.length) {
    return <div className="text-sm text-slate-500 dark:text-white/55 italic">No empathy data yet — Wujdān&apos;s daily cycle runs at 05:00 UTC.</div>;
  }

  const teamPath = buildPath(teamSeries, width, height, padX, padY, yMin, yMax);
  const gridLines = [25, 50, 75];

  const toggle = (p) => {
    setHidden((cur) => {
      const next = new Set(cur);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  const delta = data.trend_delta;
  const DeltaIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta == null ? 'text-slate-400' : delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-rose-500' : 'text-slate-400';

  return (
    <div data-testid="empathy-trend-chart">
      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3 text-xs">
        <div>
          <span className="ve-eyebrow text-slate-400 dark:text-white/45 mr-2">Team avg now</span>
          <span className="font-bold text-slate-900 dark:text-white">{data.team_avg_now ?? '—'}</span>
        </div>
        <div>
          <span className="ve-eyebrow text-slate-400 dark:text-white/45 mr-2">Start of window</span>
          <span className="font-bold text-slate-900 dark:text-white">{data.team_avg_first ?? '—'}</span>
        </div>
        <div className={`inline-flex items-center gap-1 font-bold ${deltaColor}`}>
          <DeltaIcon size={13} />
          {delta == null ? '—' : (delta > 0 ? `+${delta}` : delta)}{' '}
          <span className="font-normal text-slate-400 dark:text-white/45">over {data.days}d</span>
        </div>
        <div className="ml-auto text-[11px] text-slate-400 dark:text-white/45">{data.samples} critique samples</div>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none" role="img" aria-label="Empathy trend chart">
        {/* Y gridlines + labels */}
        {gridLines.map((g) => {
          const t = (g - yMin) / Math.max(1, yMax - yMin);
          const y = padY + (1 - t) * (height - padY * 2);
          return (
            <g key={g}>
              <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="currentColor" strokeOpacity="0.06" strokeWidth="1" strokeDasharray="3,3" />
              <text x={padX - 6} y={y + 3} textAnchor="end" fontSize="9" fill="currentColor" fillOpacity="0.4">{g}</text>
            </g>
          );
        })}

        {/* Per-persona lines (drawn behind team line) */}
        {personas.map((p, idx) => {
          if (hidden.has(p)) return null;
          const path = buildPath(personaSeries[p] || [], width, height, padX, padY, yMin, yMax);
          if (!path) return null;
          return (
            <path
              key={p}
              d={path}
              fill="none"
              stroke={PALETTE[idx % PALETTE.length]}
              strokeWidth="1.1"
              strokeOpacity="0.55"
              data-testid={`empathy-line-${p}`}
            />
          );
        })}

        {/* Team line — gold, thicker, on top */}
        {teamPath && (
          <path
            d={teamPath}
            fill="none"
            stroke="#8B3FC5"
            strokeWidth="2.4"
            data-testid="empathy-team-line"
            filter="url(#empathy-glow)"
          />
        )}
        <defs>
          <filter id="empathy-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* X-axis labels — sparse */}
        {dates.length > 0 && (
          <>
            <text x={padX} y={height - 3} fontSize="9" fill="currentColor" fillOpacity="0.45">{fmtDay(dates[0])}</text>
            {dates.length > 6 && (
              <text x={width / 2} y={height - 3} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity="0.45">
                {fmtDay(dates[Math.floor(dates.length / 2)])}
              </text>
            )}
            <text x={width - padX} y={height - 3} textAnchor="end" fontSize="9" fill="currentColor" fillOpacity="0.45">{fmtDay(dates[dates.length - 1])}</text>
          </>
        )}
      </svg>

      {/* Persona legend / toggle */}
      {personas.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <button
            onClick={() => toggle('__team')}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border border-[#8B3FC5] bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]"
            data-testid="empathy-legend-team"
          >
            <span className="inline-block w-3 h-0.5 bg-[#8B3FC5]" /> Team
          </button>
          {personas.map((p, idx) => {
            const isHidden = hidden.has(p);
            return (
              <button
                key={p}
                onClick={() => toggle(p)}
                data-testid={`empathy-legend-${p}`}
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border transition ${
                  isHidden
                    ? 'border-slate-200 dark:border-white/10 text-slate-400 dark:text-white/35 line-through'
                    : 'border-slate-200 dark:border-white/10 text-slate-700 dark:text-white/75 hover:border-[#8B3FC5]/60'
                }`}
              >
                <span className="inline-block w-3 h-0.5" style={{ background: PALETTE[idx % PALETTE.length] }} />
                {p}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
